import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createTransform,
  persistReducer,
  persistStore,
} from "redux-persist";
import type { PersistConfig } from "redux-persist";
import STORE from "redux-persist/lib/storage";
import commonSlice, { resetOnRefresh } from "./slices/commonSlice";
import authSlice from "./slices/authSlice";
import paymentsSlice, {
  dropStaleAgreementSignature,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  resetLogoOnRefresh,
  resetPaymentConfigs,
  setIsPaymentProcessing,
} from "./slices/paymentsSlice";
import documentsSlice from "./slices/documentsSlice";
import dashboardSlice from "./slices/dashboardSlice";
import subscriptionSlice, {
  resetSubscriptionState,
} from "./slices/subscriptionSlice";
import paymentMethodSlice from "./slices/paymentMethodSlice";
import actionSlice from "./slices/actionSlice";
import aiSlice from "./slices/aiSlice";
import { customMiddleware } from "./customMiddleWare";

// isDashPreview only says "a full-screen preview is open right now", and the
// layout uses it to hide the mobile header and footer. Remembering it between
// visits means one value left behind strands the customer with no navigation
// on every page until something happens to clear it.
const dashboardPersistConfig = {
  key: "dashboard",
  storage: STORE,
  blacklist: [
    "isDashPreview",
    "portalSettingsStatus",
    "portalSettingsClusterId",
    "portalSettingsRequestId",
    "stateDisclaimerByKey",
  ],
};

const reducer = combineReducers({
  common: commonSlice,
  auth: authSlice,
  payment: paymentsSlice,
  document: documentsSlice,
  dashboard: persistReducer(dashboardPersistConfig, dashboardSlice),
  subscription: subscriptionSlice,
  paymentMethod: paymentMethodSlice,
  action: actionSlice,
  ai: aiSlice,
});

type AppState = ReturnType<typeof reducer>;

// Account permissions are authorization inputs and must be revalidated for
// every browser session. Persisting them could briefly restore an old "Y"
// after an administrator has disabled a feature.
const accountPermissionTransform = createTransform<
  any,
  any,
  AppState,
  AppState
>(
  (inboundState: any) => ({
    ...inboundState,
    accountPermission: {},
    accountPermissionStatus: "idle",
    accountPermissionClusterId: null,
    accountPermissionRequestId: null,
  }),
  (outboundState: any) => ({
    ...outboundState,
    accountPermission: {},
    accountPermissionStatus: "idle",
    accountPermissionClusterId: null,
    accountPermissionRequestId: null,
  }),
  { whitelist: ["auth"] }
);

const persistConfig: PersistConfig<AppState> = {
  key: "root",
  storage: STORE,
  transforms: [accountPermissionTransform],
  // "ai" holds the assistant transcript, which quotes the customer's balance,
  // payment amounts and due dates. It survives navigation because it lives in
  // the store; it must not survive the browser, so it is kept out of storage.
  // "dashboard" is excluded here because it has its own config below; the
  // root persistor would otherwise save and restore the whole slice and
  // undo that exclusion.
  blacklist: [
    "onSetShowDisclosure",
    "onSetFinalDisclosureLoading",
    "ai",
    "dashboard",
  ],
};

const persistedReducer = persistReducer<AppState>(persistConfig, reducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(customMiddleware),
});

export const persistor = persistStore(store, null, () => {
  // This will be triggered once the state has been rehydrated
  store.dispatch(resetSubscriptionState()); // Resetting subscription state after rehydration
  store.dispatch(resetOnRefresh()); // Dispatching the reset action after rehydration
  store.dispatch(resetDuplicateOrder());
  store.dispatch(resetPaymentConfigs());
  store.dispatch(setIsPaymentProcessing(false));
  store.dispatch(resetCheckDuplicatePayment());
  store.dispatch(resetLogoOnRefresh());
  // Before PersistGate renders anything, so a leftover signature never shows.
  store.dispatch(
    dropStaleAgreementSignature({
      pathname: window.location.pathname,
      search: window.location.search,
    })
  );
});

export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
